import { useState, useEffect, useRef } from 'react'
import { loadCalmingTechniques, type CalmingTechnique } from '@/services/content'
import SOSTechniqueCard from './SOSTechniqueCard'
import SOSBreathingTimer from './SOSBreathingTimer'
import SOSQuickLog from './SOSQuickLog'
import styles from './SOSOverlay.module.css'

type Phase = 'technique' | 'breathing' | 'log'

interface Props {
  onClose: () => void
}

export default function SOSOverlay({ onClose }: Props) {
  const [phase, setPhase] = useState<Phase>('technique')
  const [techniques, setTechniques] = useState<CalmingTechnique[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [techniqueUsed, setTechniqueUsed] = useState('')
  const startTimeRef = useRef(Date.now())

  useEffect(() => {
    loadCalmingTechniques().then(setTechniques)
  }, [])

  useEffect(() => {
    // Prevent body scroll when overlay is open
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  const handleNextTechnique = () => {
    if (currentIndex < techniques.length - 1) {
      setCurrentIndex(currentIndex + 1)
    }
  }

  const handlePrevTechnique = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1)
    }
  }

  const handleTryBreathing = () => {
    if (techniques[currentIndex]) {
      setTechniqueUsed(techniques[currentIndex].title)
    }
    setPhase('breathing')
  }

  const handleBreathingDone = () => {
    setPhase('log')
  }

  const handleLogComplete = () => {
    onClose()
  }

  const handleSkipLog = () => {
    onClose()
  }

  const durationSeconds = Math.round((Date.now() - startTimeRef.current) / 1000)

  return (
    <div className={styles.overlay}>
      <button className={styles.closeButton} onClick={onClose} aria-label="Close SOS">
        &times;
      </button>

      <div className={styles.content}>
        {phase === 'technique' && techniques.length > 0 && (
          <SOSTechniqueCard
            technique={techniques[currentIndex]}
            currentIndex={currentIndex}
            total={techniques.length}
            onNext={handleNextTechnique}
            onPrev={handlePrevTechnique}
            onTryBreathing={handleTryBreathing}
          />
        )}

        {phase === 'breathing' && (
          <SOSBreathingTimer onDone={handleBreathingDone} />
        )}

        {phase === 'log' && (
          <SOSQuickLog
            techniqueUsed={techniqueUsed}
            durationSeconds={durationSeconds}
            onSave={handleLogComplete}
            onSkip={handleSkipLog}
          />
        )}
      </div>
    </div>
  )
}
